import { useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { DriverTypeAhead } from "./TypeAheadComponents"
import { useQuery } from '@apollo/client'
import Select from "react-select"
import gql from 'graphql-tag'
import DatetimeRenderer from "../../Material/DatetimeRenderer"

const AllCarsQuery = gql`query AllCars($searchTerm: String, $id: ID){
    dataList: allCars(searchTerm: $searchTerm, id_Iexact: $id, first: 30, orderBy:["-id"]){
        edges{
            node{
                id pk vin customName make model year
            }
        }
    }
    
}`

const AllIncidentsQuery = gql`query AllIncidents($searchTerm: String, $id: ID){
    dataList: allIncidents(searchTerm: $searchTerm, id: $id, first: 30, orderBy:["-id"]){
        edges{
            node{
                id 
                pk
                title
            }
        }
    }
}`

const AllLiabilityClaimsQuery = gql`query AllLiabilityClaims($searchTerm: String, $id: ID, $incidentId: ID, $isArchived: Boolean){
    dataList: allLiabilityClaims(searchTerm: $searchTerm, id_Icontains: $id, incidentId: $incidentId, isArchived: $isArchived, first: 30, orderBy:["-id"]){
        edges{
            node{
                id
                pk
                externalId
                incident {
                    id
                    title
                }
            }
        }
    }
}`

const AllSubrogationClaimsQuery = gql`query AllSubrogationClaims($searchTerm: String, $id: ID, $incidentId: ID, $isArchived: Boolean){
    dataList: allSubrogationClaims(searchTerm: $searchTerm, id_Icontains: $id, incidentId: $incidentId, isArchived: $isArchived, first: 30, orderBy:["-id"]){
        edges{
            node{
                id
                pk
                externalId
                incident {
                    id
                    title
                }
            }
        }
    }
}`


const AllCarIssuesQuery = gql`query AllCarIssues($searchTerm: String, $id: ID){
    dataList: allCarIssues(searchTerm: $searchTerm, id: $id, first: 30, orderBy:["-id"]){
        edges{
            node{
                id
                pk
                issueCategory { id category }
                car{ id pk year make model }
            }
        }
    }
}`

const AllDriversQuery = gql`query AllDrivers($searchTerm: String, $id: ID){
    dataList: allDrivers(searchTerm: $searchTerm, id: $id, first: 30, orderBy:["-id"]){
        edges{
            node{ 
                id pk name branch{ id name }
            }
        }
    }
    
}`

export const CUSTOM_FK_CONFIG = {
    "driver": {
        getLink: (node) => `/driver/${node?.grapheneId}`,
        getLabel: (node) => node?.valueString || `Driver #${node?.value}` || "View Driver",
        inputComponent: (props) => <TypeAheadInput query={AllDriversQuery} renderLabel={(node) => `#${node.pk} · ${node.name} · ${node.branch.name ?? ""}`} placeholder="Select a driver" {...props} />
    },
    "car": {
        getLink: (node) => `/car/${node?.value}`,
        getLabel: (node) => node?.valueString || `Car #${node?.value}` || "View Car",
        inputComponent: (props) => <TypeAheadInput query={AllCarsQuery} renderLabel={(node) => `#${node.pk} · ${node.year} ${node.make} ${node.model} · ${node.vin}`} placeholder="Select a car" {...props} />
    },
    "incident": {
        getLink: (node) => `/incident/${node?.grapheneId}`,
        getLabel: (node) => node?.valueString || `Incident #${node?.value}` || "View Incident",
        inputComponent: (props) => <TypeAheadInput query={AllIncidentsQuery} renderLabel={(node) => `#${node.pk} · ${node.title}`} placeholder="Select an incident" {...props} />
    },
    "carissues": {
        getLink: (node) => `/car-issues/${node?.grapheneId}`,
        getLabel: (node) => node?.valueString || `Car Issue #${node?.value}` || "View Car Issue",
        inputComponent: (props) => <TypeAheadInput query={AllCarIssuesQuery} renderLabel={(node) => `#${node.pk} · ${node.issueCategory?.category || "No Category Selected"} (${node.car?.year} ${node.car?.make} ${node.car?.model} #${node.car?.pk})`} placeholder="Select a car issue" {...props} />
    },
    "liabilityclaim": {
        getLink: (node) => `/liability-claims/${node?.grapheneId}`,
        getLabel: (node) => node?.valueString || `Liability Claim #${node?.value}` || "View Liability Claim",
        inputComponent: (props) => <TypeAheadInput query={AllLiabilityClaimsQuery} renderLabel={(node) => `#${node.pk} · ${node.externalId || "No Claim #"} · ${node.incident?.title || "No Reference"}`} placeholder="Select a liability claim" {...props} />
    },
    "subrogationclaim": {
        getLink: (node) => `/subrogation-claims/${node?.grapheneId}`,
        getLabel: (node) => node?.valueString || `Subrogation Claim #${node?.value}` || "View Subrogation Claim",
        inputComponent: (props) => <TypeAheadInput query={AllSubrogationClaimsQuery} renderLabel={(node) => `#${node.pk} · ${node.externalId || "No Claim #"} · ${node.incident?.title || "No Reference"}`} placeholder="Select a subrogation claim" {...props} />
    },
}

export const renderCustomColumn = (columnValueNode) => {
    if (!columnValueNode) {
        return <span className={"text-muted"}>--</span>
    }

    if (columnValueNode?.column?.columnType === "FK_OBJECT") {
        let model = columnValueNode?.column?.contentType?.model || ""
        let config = CUSTOM_FK_CONFIG[model]
        if (config) {
            return (
                <Link to={config.getLink(columnValueNode)} target="_blank">
                    {config.getLabel(columnValueNode)}
                </Link>
            )
        } else {
            return <span className={columnValueNode?.valueString ? "" : "text-muted"}>{columnValueNode?.valueString || "--"}</span>
        }
    } else if (columnValueNode?.column?.columnType === "URL" && columnValueNode?.valueString) {
        const url = columnValueNode.valueString
        const displayText = url.length > 35 ? url.slice(0, 35) + "…" : url
        return (
            <a href={url} target="_blank" rel="noopener noreferrer" title={url}>
                {displayText}
            </a>
        )
    } else if (columnValueNode?.column?.columnType === "BOOLEAN") {
        const isTrue = columnValueNode?.valueString === "true" || columnValueNode?.valueString === "True"
        return <span>{isTrue ? "Yes" : "No"}</span>
    } else if (columnValueNode?.column?.columnType === "TIME" && columnValueNode?.valueString) {
        // Show HH:MM only, strip seconds
        return <span>{columnValueNode.valueString.slice(0, 5)}</span>
    } else if (columnValueNode?.column?.columnType === "DATE" && columnValueNode?.valueString) {
        // Extract just the date part (YYYY-MM-DD) from "YYYY-MM-DD HH:MM:SS"
        const dateOnly = columnValueNode.valueString.split(" ")[0]
        return <DatetimeRenderer datetime={dateOnly} />
    } else if (columnValueNode?.column?.columnType === "DATETIME" && columnValueNode?.valueString) {
        return <DatetimeRenderer datetime={columnValueNode.valueString} />
    }

    return <span className={columnValueNode?.valueString ? "" : "text-muted"}>{columnValueNode?.valueString || "--"}</span>
}

export const ForeignKeyInput = ({ model, onChange, defaultValue, isDisabled, queryVariables }) => {
    const config = CUSTOM_FK_CONFIG[model]

    if (config) {
        const InputComponent = config.inputComponent
        return <InputComponent defaultValue={defaultValue} onChange={onChange} isDisabled={isDisabled} queryVariables={queryVariables} />
    }
}

const normalizeDefaultValue = (defaultValue) => {
    if (Array.isArray(defaultValue)) {
        return defaultValue[0] || null
    }
    return defaultValue || null
}

const getDefaultValueId = (defaultValue) => {
    const normalizedDefault = normalizeDefaultValue(defaultValue)
    if (!normalizedDefault) return null
    if (typeof normalizedDefault === "string") return normalizedDefault
    if (typeof normalizedDefault === "number") return String(normalizedDefault)
    return normalizedDefault.id || normalizedDefault.value || null
}

export const TypeAheadInput = ({ query, onChange, renderLabel = null, placeholder = "Select an option", defaultValue, isDisabled, queryVariables = {} }) => {
    const [searchTerm, setSearchTerm] = useState("")
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("")
    const [selectedOption, setSelectedOption] = useState(null)
    const { loading, error, data } = useQuery(query, {
        variables: { ...queryVariables, searchTerm: debouncedSearchTerm, id: defaultValue && isDisabled ? defaultValue : null }
    })

    const options = useMemo(() => (
        data?.dataList?.edges?.length > 0 ? data.dataList.edges.map(object => ({
            value: object.node.id,
            label: renderLabel ? renderLabel(object.node) : object.node.id,
            node: object.node,
        })) : []
    ), [data, renderLabel])

    const normalizedDefault = normalizeDefaultValue(defaultValue)
    const defaultValueId = getDefaultValueId(defaultValue)

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm.trim())
        }, 300)

        return () => clearTimeout(timeoutId)
    }, [searchTerm])

    useEffect(() => {
        if (!normalizedDefault) {
            setSelectedOption(prev => (prev ? null : prev))
            return
        }

        const matchedOption = options.find(option => option.value === defaultValueId)
        if (matchedOption) {
            setSelectedOption(prev => (prev?.value === matchedOption.value ? prev : matchedOption))
            return
        }

        if (typeof normalizedDefault === "object") {
            const fallbackOption = {
                value: normalizedDefault.id || normalizedDefault.value,
                label: renderLabel ? renderLabel(normalizedDefault) : normalizedDefault.title || normalizedDefault.name || normalizedDefault.id,
                node: normalizedDefault,
            }
            setSelectedOption(prev => (prev?.value === fallbackOption.value ? prev : fallbackOption))
        }
    }, [defaultValueId, normalizedDefault, options, renderLabel])

    return <Select
        options={options}
        placeholder={error ? `Error! ${error.message}` : placeholder}
        onInputChange={(inputValue, { action }) => {
            if (action === "input-change") {
                setSearchTerm(inputValue)
            }
        }}
        onChange={(selected) => {
            setSelectedOption(selected || null)
            onChange(selected ? selected.value : null)
        }}
        value={selectedOption}
        isDisabled={Boolean(isDisabled && defaultValue) || Boolean(error)}
        isLoading={loading}
        filterOption={() => true}
        noOptionsMessage={() => {
            if (loading) return "Loading..."
            if (debouncedSearchTerm) return "No matching results"
            return "Start typing to search"
        }}
        isClearable
    />
}
